import { Button, makeStyles, TextField } from '@material-ui/core';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Autocomplete } from '@material-ui/lab';
import { useClients, useMaterials } from 'hooks';
import YearMonthPeriod from 'components/YearMonthPeriod';
import { DEFAULT_ITEMS_PER_SELECT } from 'tableFilterContext';
import NextIcon from 'components/Icons/NextIcon';
import { colors } from 'theme';
import { Client, Material } from 'types';
import { transformObjectId } from 'services/apiPlatformTransformer';
import { FilterFormProps } from '../types';

const useStyles = makeStyles({
  form: {
    width: '100%',
    display: 'flex',
    flexFlow: 'row',
    alignItems: 'center',
    paddingBottom: '2.5em',
  },
  fieldLine: {
    marginTop: 8,
    width: '25%',
    margin: '1vw',
    '& label': {
      color: '#949FAC',
    },

    '& > div': {
      width: '100%',
    },

    '& span': {
      fontSize: 18,
      marginBottom: 5,
    },
  },
  selectWithError: {
    '& > div': {
      width: '100%',
    },
  },
  switch: {
    marginTop: 20,
    marginBottom: 0,
  },
  sharedLine: {
    display: 'flex',
    justifyContent: 'space-between',

    '& > div': {
      width: 214,
    },
  },
  searchButton: {
    fontSize: 14,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
  },
  searchButtonIcon: {
    marginLeft: '10px',
    width: 17,
    height: 17,
  },
  textField: {
    '& fieldset': {
      borderColor: colors.neutralGray,
    },
  },
});

const FilterForm: React.FC<FilterFormProps> = ({
  filters,
  onSubmit,
  onChangePeriod,
  defaultPeriod,
  minStartDate,
  maxEndDate,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('translation', { keyPrefix: 'conditionedStockByClientPage.form' });
  const [clients] = useClients(DEFAULT_ITEMS_PER_SELECT);
  const materials = useMaterials({ itemsPerPage: String(DEFAULT_ITEMS_PER_SELECT) });
  const defaultValues = {
    clientId: filters?.clientIds?.[0] ?? '',
    materialId: filters?.materialIds?.[0] ?? '',
  };

  const { control, handleSubmit } = useForm({
    mode: 'onBlur',
    defaultValues,
  });

  return (
    <>
      <form id="conditioned_stocks_by_client_filter_form" onSubmit={handleSubmit(onSubmit)} className={classes.form}>
        <div className={classes.fieldLine}>
          <span>
            <Controller
              name="clientId"
              control={control}
              render={({ field: { ref, onChange, ...fieldProps } }) => (
                <Autocomplete
                  options={clients ?? []}
                  size="small"
                  getOptionLabel={client => client.shortName}
                  noOptionsText={t('noResults')}
                  onChange={(_e, value: Client | null) => {
                    onChange({ target: { value: transformObjectId(value) } });
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      data-qa-id="client-filter"
                      className={classes.textField}
                      {...fieldProps}
                      placeholder={t('filters.client')}
                      variant="outlined"
                    />
                  )}
                />
              )}
            />
          </span>
        </div>
        <div className={classes.fieldLine}>
          <span>
            <Controller
              name="materialId"
              control={control}
              render={({ field: { ref, onChange, ...fieldProps } }) => (
                <Autocomplete
                  options={materials ?? []}
                  size="small"
                  getOptionLabel={material => material.shortName}
                  noOptionsText={t('noResults')}
                  onChange={(_e, value: Material | null) => {
                    onChange({ target: { value: transformObjectId(value) } });
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      data-qa-id="material-filter"
                      className={classes.textField}
                      {...fieldProps}
                      placeholder={t('filters.material')}
                      variant="outlined"
                    />
                  )}
                />
              )}
            />
          </span>
        </div>
        <div className={classes.fieldLine}>
          <YearMonthPeriod
            defaultValues={defaultPeriod}
            minStartDate={minStartDate}
            maxEndDate={maxEndDate}
            onChange={onChangePeriod}
          />
        </div>
        <div className={classes.fieldLine}>
          <Button style={{ marginTop: 12 }} size="small" type="submit" variant="text">
            <div className={classes.searchButton}>{t('searchButton')}</div>
            <NextIcon className={classes.searchButtonIcon} />
          </Button>
        </div>
      </form>
    </>
  );
};

export default FilterForm;
